import Reveal from "@/components/feature/Reveal";
import SectionHeading from "@/components/feature/SectionHeading";
import { useInstagramFeed, type InstagramFeedItem } from "@/hooks/useInstagramFeed";
import { clinic } from "@/mocks/clinic";

const isVideo = (mediaType?: string): boolean => {
  const type = (mediaType ?? "").toUpperCase();
  return type === "VIDEO" || type === "REELS";
};

function InstagramCard({ item }: { item: InstagramFeedItem }) {
  return (
    <a
      href={item.permalink}
      target="_blank"
      rel="noopener noreferrer"
      className="group relative block w-full overflow-hidden rounded-lg bg-background-200"
      aria-label="Instagramの投稿を新しいタブで開く"
    >
      <div className="relative aspect-square w-full overflow-hidden">
        <img
          src={item.image_url}
          alt="渋谷美容皮膚科クリニックのInstagram投稿"
          title="渋谷美容皮膚科クリニックのInstagram投稿"
          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
        />
        <span className="pointer-events-none absolute inset-0 bg-foreground-950/0 transition-colors group-hover:bg-foreground-950/15" />
        {isVideo(item.media_type) ? (
          <span className="pointer-events-none absolute right-2 top-2 flex h-6 w-6 items-center justify-center rounded-full bg-foreground-950/45 text-background-50">
            <i className="ri-vidicon-line text-xs" aria-hidden />
          </span>
        ) : null}
      </div>
    </a>
  );
}

function InstagramSkeleton() {
  return (
    <>
      {Array.from({ length: 6 }).map((_, index) => (
        <div
          key={`instagram-skeleton-${index}`}
          className="aspect-square w-full animate-pulse rounded-lg bg-background-200/70"
          aria-hidden
        />
      ))}
    </>
  );
}

export default function InstagramSection() {
  const { items, status } = useInstagramFeed(6);
  const showGrid = status === "ready" && items.length > 0;

  return (
    <section className="bg-background-50 py-16 md:py-24">
      <div className="u-container">
        <Reveal>
          <div className="flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between">
            <SectionHeading en="INSTAGRAM" jp="SBDCの最新情報をInstagramで" />
            <a
              href={clinic.instagramUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="group inline-flex items-center gap-3 whitespace-nowrap rounded-md border border-foreground-950/20 bg-background-50 px-6 py-3 text-[13px] text-foreground-900 transition-colors hover:bg-secondary-100"
            >
              <i className="ri-instagram-line text-base text-accent-700" aria-hidden />
              Instagramを見る
              <i className="ri-arrow-right-line transition-transform group-hover:translate-x-1" aria-hidden />
            </a>
          </div>
        </Reveal>

        <Reveal delay={120}>
          <div className="mt-12 grid grid-cols-2 gap-3 md:mt-14 md:grid-cols-3 md:gap-4">
            {status === "loading" ? <InstagramSkeleton /> : null}
            {showGrid ? items.map((item) => <InstagramCard key={item.id} item={item} />) : null}
          </div>
        </Reveal>
      </div>
    </section>
  );
}